// Timer (P0.3). O tempo vem do servidor: elapsed_seconds + (agora − running_since).
// As transições abaixo espelham as funções do banco (0002_timer.sql) — o banco em
// memória (testes) e a tela otimista usam as mesmas regras.
import type { Task } from './task';

export function elapsedSeconds(task: Task, nowMs: number): number {
  let total = task.elapsed_seconds;
  if (task.status === 'running' && task.running_since) {
    total += Math.max(0, Math.floor((nowMs - Date.parse(task.running_since)) / 1000));
  }
  return total;
}

export type TimerMode = 'countdown' | 'overtime' | 'stopwatch';

export interface TimerView {
  mode: TimerMode;
  /** Segundos exibidos (restantes, extras ou decorridos). */
  seconds: number;
  /** "25:00", "+04:12", "03:07", "1:02:03". */
  text: string;
  elapsed: number;
}

export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function timerView(task: Task, nowMs: number): TimerView {
  const elapsed = elapsedSeconds(task, nowMs);
  if (task.estimate_minutes == null) {
    return { mode: 'stopwatch', seconds: elapsed, text: formatClock(elapsed), elapsed };
  }
  const remaining = task.estimate_minutes * 60 - elapsed;
  if (remaining >= 0) {
    return { mode: 'countdown', seconds: remaining, text: formatClock(remaining), elapsed };
  }
  return { mode: 'overtime', seconds: -remaining, text: `+${formatClock(-remaining)}`, elapsed };
}

/** O timer cruzou o zero entre duas leituras? (o aviso sonoro toca uma única vez) */
export function crossedEstimate(task: Task, prevElapsed: number, nextElapsed: number): boolean {
  if (task.estimate_minutes == null) return false;
  const limit = task.estimate_minutes * 60;
  return prevElapsed < limit && nextElapsed >= limit;
}

export type FinishKind = 'early' | 'late' | 'onTime' | 'noEstimate';

export interface FinishResult {
  kind: FinishKind;
  /** minutos de diferença (arredondado ao minuto mais próximo — ASM-012). */
  minutes: number;
}

export function finishResult(elapsed: number, estimateMinutes: number | null): FinishResult {
  if (estimateMinutes == null) return { kind: 'noEstimate', minutes: Math.round(elapsed / 60) };
  const diff = estimateMinutes * 60 - elapsed;
  const minutes = Math.round(Math.abs(diff) / 60);
  if (minutes < 1) return { kind: 'onTime', minutes: 0 };
  return { kind: diff > 0 ? 'early' : 'late', minutes };
}

// ---------- transições (espelho de task_start / task_pause / task_finish) ----------

function pausedAt(task: Task, nowIso: string): Task {
  return {
    ...task,
    status: 'paused',
    elapsed_seconds: elapsedSeconds(task, Date.parse(nowIso)),
    running_since: null,
    updated_at: nowIso,
  };
}

/** Inicia `id`; a que estava rodando fica pausada com o tempo preservado. */
export function applyStart(tasks: readonly Task[], id: string, nowIso: string): Task[] {
  const target = tasks.find((t) => t.id === id);
  if (!target || target.status === 'done' || target.status === 'running') return [...tasks];
  return tasks.map((t) => {
    if (t.id === id) return { ...t, status: 'running', running_since: nowIso, updated_at: nowIso };
    if (t.status === 'running') return pausedAt(t, nowIso);
    return t;
  });
}

export function applyPause(tasks: readonly Task[], id: string, nowIso: string): Task[] {
  return tasks.map((t) => (t.id === id && t.status === 'running' ? pausedAt(t, nowIso) : t));
}

export function applyFinish(tasks: readonly Task[], id: string, nowIso: string): Task[] {
  return tasks.map((t) =>
    t.id === id && t.status !== 'done'
      ? {
          ...t,
          status: 'done',
          elapsed_seconds: elapsedSeconds(t, Date.parse(nowIso)),
          running_since: null,
          completed_at: nowIso,
          updated_at: nowIso,
        }
      : t
  );
}
